import React, { useState, useMemo } from 'react';
import DeckSelectionScreen from './components/DeckSelectionScreen';
import CardSelector from './components/CardSelector';
import CardDisplay from './components/CardDisplay';
import ThirdCardDisplay from './components/ThirdCardDisplay';
import CardSpread from './components/CardSpread';
import { generateThirdCard } from './api/generateThirdCard.js';
import { useCards } from './hooks/useCards';

/**
 * Root component for the Third Card application.
 *
 * Users first choose their selection method (physical deck or digital),
 * then select two Tarot cards. The app attempts to fetch a cached
 * Third Card from the database (D1). If none exists, the API handler
 * will call OpenAI to generate a new card and store it before returning it.
 */
export default function App() {
  const [selectionMethod, setSelectionMethod] = useState(null); // 'physical' or 'digital'
  const [card1, setCard1] = useState(null);
  const [card2, setCard2] = useState(null);
  const [thirdCard, setThirdCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Load cards once at app level
  const { cards: allCards, loading: cardsLoading } = useCards();

  // Memoize selected cards array to prevent unnecessary recalculation
  const selectedCards = useMemo(() => {
    return [card1, card2].filter(Boolean);
  }, [card1, card2]);

  // Handler for card spread selections
  const handleSpreadCardSelect = (card) => {
    if (!card1) {
      setCard1({ ...card, orientation: 'upright' });
    } else if (!card2) {
      setCard2({ ...card, orientation: 'upright' });
    }
    // If both are selected, ignore further clicks (user can use dropdowns to change)
  };
  
  // Handler to switch from physical to virtual deck
  const handleSwitchToVirtualDeck = () => {
    setSelectionMethod('digital');
    // Reset selected cards when switching
    setCard1(null);
    setCard2(null);
  };
  
  // Handler to switch from virtual to physical deck
  const handleSwitchToPhysicalDeck = () => {
    setSelectionMethod('physical');
    // Reset selected cards when switching
    setCard1(null);
    setCard2(null);
  };
  
  const handleGenerate = async () => {
    if (!card1 || !card2) return;
    
    setLoading(true);
    setThirdCard(null);
    setError(null);
    
    try {
      const result = await generateThirdCard(card1, card2);
      setThirdCard(result);
      setError(null);
    } catch (err) {
      console.error('Error generating third card:', err);
      setError({
        message: err.message || 'Unable to generate the third card',
        details: 'The Oracle encountered difficulty in revealing your third card. This may be due to AI service limitations or connectivity issues.'
      });
      setThirdCard(null);
    } finally {
      setLoading(false);
    }
  };

  // Handler to reset and try again after an error
  const handleTryAgain = () => {
    setError(null);
    setCard1(null);
    setCard2(null);
    setThirdCard(null);
  };

  return (
    <div className="min-h-screen">
      {/* Show deck selection screen first */}
      {!selectionMethod && (
        <DeckSelectionScreen onMethodSelect={setSelectionMethod} />
      )}
      
      {/* Show main card selection interface after method is chosen */}
      {selectionMethod && (
        <div className="card-altar">
          <h1 className="heading-primary">🜂 The Third Card Oracle 🜂</h1>
          <h2 className="heading-secondary">Where Two Paths Converge, A Third Reveals</h2>
          
          <div className="divider-sacred"></div>
          
          {/* Show dropdowns for physical deck users */}
          {selectionMethod === 'physical' && (
            <CardSelector 
              cards={allCards}
              loading={cardsLoading}
              onSelectCard1={setCard1} 
              onSelectCard2={setCard2}
              onSwitchToVirtualDeck={handleSwitchToVirtualDeck}
            />
          )}
          
          {/* Show card spread for digital users */}
          {selectionMethod === 'digital' && allCards.length > 0 && (
            <CardSpread 
              cards={allCards}
              onCardSelect={handleSpreadCardSelect}
              selectedCards={selectedCards}
              onSwitchToPhysicalDeck={handleSwitchToPhysicalDeck}
            />
          )}
          
          {/* Show selected cards as they are chosen */}
          {card1 && (
            <div className="flex flex-col lg:flex-row justify-around mt-8 gap-6">
              <div className="flex-1 card-glow-static">
                <h3 className="heading-tertiary">First Card</h3>
                <CardDisplay card={card1} />
              </div>
              {card2 && (
                <div className="flex-1 card-glow-static">
                  <h3 className="heading-tertiary">Second Card</h3>
                  <CardDisplay card={card2} />
                </div>
              )}
            </div>
          )}
          
          {/* Show button only when both cards are selected */}
          {card1 && card2 && (
            <div className="text-center mt-8">
              <button
                className="btn-mystical"
                onClick={handleGenerate}
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-mystical">Invoking the Oracle...</span>
                ) : (
                  'Reveal the Third Card'
                )}
              </button>
            </div>
          )}
          
          {loading && !thirdCard && (
            <div className="card-altar text-center mt-8">
              <div className="third-card-loading">
                <div className="loading-spinner"></div>
                <p className="loading-text">Consulting the Oracle of the Third Path...</p>
                <p className="text-sm text-gray-400 mt-2">Weaving mystical synthesis... (30-45 seconds)</p>
              </div>
            </div>
          )}
          
          {/* Error state with retry option */}
          {error && !loading && (
            <div className="card-altar text-center mt-8 border-2 border-red-900/50">
              <div className="mb-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="heading-tertiary text-red-400 mb-4">The Oracle is Silent</h3>
                <p className="body-text text-amber-200 mb-3">
                  {error.message}
                </p>
                <p className="text-sm text-gray-400 mb-6 max-w-xl mx-auto">
                  {error.details}
                </p>
              </div>
              
              <div className="flex gap-4 justify-center">
                <button
                  className="btn-mystical"
                  onClick={handleGenerate}
                >
                  ✨ Try Again with Same Cards
                </button>
                <button
                  className="px-6 py-3 bg-purple-900/60 hover:bg-purple-800/80 text-amber-300 rounded-lg border border-amber-600/40 transition-all duration-300 hover:border-amber-500/60 font-semibold"
                  onClick={handleTryAgain}
                >
                  🔄 Choose Different Cards
                </button>
              </div>
            </div>
          )}
          
          {thirdCard && !error && (
            <>
              <div className="divider-sacred mt-8"></div>
              <ThirdCardDisplay card={thirdCard} card1={card1} card2={card2} />
            </>
          )}
        </div>
      )}
    </div>
  );
}